import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  Avatar,
  Divider,
  Stack,
  Alert,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup, onAuthStateChanged, GoogleAuthProvider, signOut } from "firebase/auth";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TwitterIcon from "@mui/icons-material/Twitter";
import { SiTiktok } from "react-icons/si";
import IconButton from "@mui/material/IconButton";

const countryOptions = [
  { code: "CO", name: "Colombia" },
  { code: "MX", name: "México" },
  { code: "PA", name: "Panamá" },
];

const citiesByCountry = {
  CO: [
  "Armenia",
  "Barranquilla",
  "Bogotá",
  "Bucaramanga",
  "Cali",
  "Cartagena",
  "Cúcuta",
  "Florencia",
  "Ibagué",
  "Manizales",
  "Medellín",
  "Montería",
  "Neiva",
  "Pasto",
  "Pereira",
  "Popayán",
  "Quibdó",
  "Riohacha",
  "Santa Marta",
  "Sincelejo",
  "Tunja",
  "Valledupar",
  "Villavicencio"
],

  MX: [
  "Acapulco",
  "Aguascalientes",
  "Benito Juárez (Cancún)",
  "Campeche",
  "Celaya",
  "Chihuahua",
  "Chimalhuacán",
  "Ciudad de México",
  "Ciudad Juárez",
  "Culiacán",
  "Cuernavaca",
  "Durango",
  "Ecatepec de Morelos",
  "Guadalajara",
  "Hermosillo",
  "León",
  "Mexicali",
  "Mérida",
  "Monterrey",
  "Morelia",
  "Naucalpan de Juárez",
  "Nezahualcóyotl",
  "Puebla",
  "Querétaro",
  "Reynosa",
  "Saltillo",
  "San Luis Potosí",
  "Tacuba",
  "Tampico",
  "Tijuana",
  "Tlaquepaque",
  "Toluca",
  "Torreón",
  "Zapopan"
],

  PA: [
    "Ciudad de Panamá", "Colón", "David", "Santiago", "Chitré", "La Chorrera", "Penonomé", "Aguadulce"
  ],
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [companyData, setCompanyData] = useState({
    company: "",
    address: "",
    city: "",
    country: "",
    tax_id: "",
  });
  const [success, setSuccess] = useState(""); // Agrega este estado arriba
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Mensaje de sesión expirada (viene del hook de inactividad)
  const sessionMessage = location.state?.message;

  // Escuchar cambios de autenticación de Firebase
  useEffect(() => {
    console.log("=== Iniciando listener de auth ===");

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("onAuthStateChanged - usuario:", user?.email || "null");

      if (user && !localStorage.getItem("user_id")) {
        // Usuario autenticado con Firebase pero sin user_id en localStorage
        // Esto significa que viene de Google login
        console.log("Usuario Firebase detectado, registrando en backend...");
        setLoadingGoogle(true);

        try {
          const response = await fetch("/api/register/google/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: user.displayName?.split(" ")[0] || "",
              lastname: user.displayName?.split(" ").slice(1).join(" ") || "",
              email: user.email,
              firebase_uid: user.uid,
            }),
          });

          const data = await response.json();
          console.log("Respuesta backend:", data, "Status:", response.status);

          if (response.ok && data.id) {
            localStorage.setItem("user_id", data.id);
            console.log("user_id guardado:", data.id);
            window.location.href = "/dashboard";
          } else if (response.ok) {
            // Usuario existe, navegar al dashboard
            console.log("Usuario existe, navegando...");
            window.location.href = "/dashboard";
          } else if (data.detail && data.detail.includes("Faltan datos de empresa")) {
            // Usuario nuevo, necesita registrar empresa
            console.log("Usuario nuevo, mostrando form de empresa");
            setGoogleUser({
              name: user.displayName?.split(" ")[0] || "",
              lastname: user.displayName?.split(" ").slice(1).join(" ") || "",
              email: user.email,
            });
            setFirebaseUser(user);
            setShowCompanyForm(true);
          } else {
            console.error("Error inesperado:", data);
            setError("Error al procesar el login. Intenta de nuevo.");
          }
        } catch (fetchErr) {
          console.error("Error en fetch al backend:", fetchErr);
          setError("Error al conectar con el servidor.");
        }

        setLoadingGoogle(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Login tradicional (email y contraseña)
  const loginWithEmail = async () => {
    setError("");
    try {
      const response = await fetch("/api/login/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) throw new Error("Correo o contraseña incorrectos.");
      const data = await response.json();
      localStorage.setItem("user_id", data.user_id);
      console.log("Login exitoso, user_id guardado:", data.user_id);
      navigate("/dashboard");
    } catch (err) {
      setError("Correo o contraseña incorrectos.");
    }
  };

  // Login/registro con Google - usa popup
  const loginWithGoogle = async () => {
    setError("");
    setLoadingGoogle(true);
    try {
      // No hacer signOut antes - causa problemas
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      console.log("Abriendo popup de Google...");
      const result = await signInWithPopup(auth, provider);
      console.log("Popup completado, usuario:", result.user?.email);

      // El onAuthStateChanged manejará el resto
      // Pero por si acaso, también procesamos aquí
      if (result.user) {
        const user = result.user;
        console.log("Registrando usuario en backend...");

        const response = await fetch("/api/register/google/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: user.displayName?.split(" ")[0] || "",
            lastname: user.displayName?.split(" ").slice(1).join(" ") || "",
            email: user.email,
            firebase_uid: user.uid,
          }),
        });

        const data = await response.json();
        console.log("Respuesta backend:", data, "Status:", response.status);

        if (response.ok && data.id) {
          localStorage.setItem("user_id", data.id);
          console.log("user_id guardado, navegando...");
          navigate("/dashboard");
        } else if (response.ok) {
          console.log("Usuario existe, navegando...");
          navigate("/dashboard");
        } else if (data.detail && data.detail.includes("Faltan datos de empresa")) {
          setGoogleUser({
            name: user.displayName?.split(" ")[0] || "",
            lastname: user.displayName?.split(" ").slice(1).join(" ") || "",
            email: user.email,
          });
          setFirebaseUser(user);
          setShowCompanyForm(true);
        } else {
          setError("Error al procesar el login.");
        }
      }
    } catch (err) {
      console.error("Error en login con Google:", err);
      console.error("Código de error:", err.code);
      console.error("Mensaje:", err.message);
      setError(`Error: ${err.message || "No se pudo iniciar sesión con Google"}`);
    } finally {
      setLoadingGoogle(false);
    }
  };

  // Enviar datos de empresa para completar registro con Google
  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      const payload = {
        name: googleUser?.name || "",
        lastname: googleUser?.lastname || "",
        email: googleUser?.email || "",
        firebase_uid: firebaseUser?.uid,
        company: {
          company: companyData.company,
          address: companyData.address,
          city: companyData.city,
          country: companyData.country,
          tax_id: companyData.tax_id,
        },
      };
      const response = await fetch("/api/register/google/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json();
        if (
          response.status === 409 &&
          typeof data.detail === "string" &&
          data.detail.includes("ya está registrada")
        ) {
          setError(data.detail);
        } else {
          let msg = "Error al registrar usuario con Google.";
          if (Array.isArray(data.detail)) {
            msg = data.detail.map(e => e.msg).join(" | ");
          } else if (typeof data.detail === "string") {
            msg = data.detail;
          }
          setError(msg);
        }
        return;
      }
      setSuccess("Usuario y empresa registrada de manera exitosa. Será redirigido al inicio de sesión en unos segundos...");
      setTimeout(() => {
        setSuccess("");
        setShowCompanyForm(false);
        navigate("/login");
      }, 3000); // 3 segundos
    } catch (err) {
      setError("Error al registrar usuario con Google.");
    }
  };

  // Obtener el código del país seleccionado
  const selectedCountryCode = countryOptions.find(
    c => c.name === companyData.country
  )?.code;

  return (
        <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: "rgba(23, 66, 136, 0.98)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 3,
          borderRadius: 5,
          minWidth: 320,
          maxWidth: 380,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          background: "#fff",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.18)",
          zIndex: 1,
        }}
      >
        <Avatar sx={{ bgcolor: "rgba(23, 66, 136, 0.98)", width: 60, height: 60, mb: 1 }}>
          <img
            src="/images/logo-nara-png.png"
            alt="Nara Logo"
            style={{ width: 76, height: 76, objectFit: "contain" }}
          />
        </Avatar>
        <Typography variant="h6" fontWeight={700} color="#1e293b" mb={0.5}>
          Nara AI Chatbots
        </Typography>
        <Typography variant="body2" color="#64748b" mb={1} align="center" fontSize={13}>
          Inicia sesión para gestionar tus flujos conversacionales.
        </Typography>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <TextField
            label="Correo"
            variant="outlined"
            size="small"
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
            sx={{
              background: "#f1f5f9",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#e0e7ef" },
                "&:hover fieldset": { borderColor: "#6366f1" },
                "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                input: { color: "#334155" },
              },
              label: { color: "#64748b" },
            }}
          />
          <TextField
            label="Contraseña"
            type="password"
            variant="outlined"
            size="small"
            fullWidth
            value={password}
            onChange={e => setPassword(e.target.value)}
            sx={{
              background: "#f1f5f9",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#e0e7ef" },
                "&:hover fieldset": { borderColor: "#6366f1" },
                "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                input: { color: "#334155" },
              },
              label: { color: "#64748b" },
            }}
          />
        </Stack>
        {sessionMessage && (
          <Alert severity="warning" sx={{ width: '100%', mt: 1, fontSize: 13 }}>
            {sessionMessage}
          </Alert>
        )}
        {error && (
          <Typography color="error" sx={{ fontSize: 13, mt: 1 }}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          fullWidth
          size="small"
          sx={{
            mt: 1,
            fontWeight: 700,
            fontSize: 15,
            background: "rgba(23, 66, 136, 0.98)",
            color: "#fff",
            borderRadius: 2,
            textTransform: "none",
            "&:hover": {
              background: "linear-gradient(90deg, #0ea5e9 0%, #6366f1 100%)",
            },
          }}
          onClick={loginWithEmail}
        >
          Iniciar sesión
        </Button>
        <Divider sx={{ width: "100%", my: 1, borderColor: "#e0e7ef" }}>o</Divider>
        <Button
          variant="outlined"
          fullWidth
          size="small"
          startIcon={<GoogleIcon />}
          sx={{
            fontWeight: 700,
            fontSize: 15,
            borderColor: "rgba(23, 66, 136, 0.98)",
            color: "rgba(23, 66, 136, 0.98)",
            background: "#f8fafc",
            borderRadius: 2,
            textTransform: "none",
            "&:hover": {
              borderColor: "#0ea5e9",
              color: "#0ea5e9",
              background: "#e0f2fe",
            },
          }}
          onClick={loginWithGoogle}
        >
          Google
        </Button>
        <Typography
          variant="caption"
          color="#64748b"
          sx={{ mt: 2, fontSize: 12 }}
        >
          ¿No tienes cuenta?{" "}
          <span
            style={{ color: "rgba(23, 66, 136, 0.98)", cursor: "pointer", fontWeight: 600 }}
            onClick={() => navigate("/register")}
          >
            Regístrate
          </span>
        </Typography>
      </Paper>
      {/* Iconos de redes sociales debajo del formulario */}
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 2 }}>
        <IconButton
          component="a"
          href="https://x.com/nara"
          target="_blank"
          rel="noopener"
          sx={{ color: "#fff", p: 0.5 }}
        >
          {/* SVG oficial de X */}
          <svg width="18" height="18" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" d="M90.5 10H110L72 54.5L120 110H90L60 76L30 110H0L48 54.5L10 10H39.5L60 34L90.5 10Z"/>
          </svg>
        </IconButton>
        <IconButton
          component="a"
          href="https://instagram.com/nara"
          target="_blank"
          rel="noopener"
          sx={{ color: "#fff", p: 0.5 }}
        >
          <InstagramIcon fontSize="small" />
        </IconButton>
        <IconButton
          component="a"
          href="https://youtube.com/nara"
          target="_blank"
          rel="noopener"
          sx={{ color: "#fff", p: 0.5 }}
        >
          <YouTubeIcon fontSize="small" />
        </IconButton>
        <IconButton
          component="a"
          href="https://tiktok.com/@nara"
          target="_blank"
          rel="noopener"
          sx={{ color: "#fff", p: 0.5 }}
        >
          <SiTiktok size={18} />
        </IconButton>
      </Box>

      {/* Formulario de empresa para usuarios de Google nuevos */}
      {showCompanyForm && (
        <Paper
          elevation={8}
          sx={{
            p: 2,
            borderRadius: 4,
            minWidth: 260,
            maxWidth: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "#fff",
            position: "absolute",
            top: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.18)",
            gap: 1,
          }}
        >
          <Typography variant="h6" mb={1} color="#6366f1" fontWeight={700}>
            Completa los datos de tu empresa
          </Typography>
          <form style={{ width: "100%" }} onSubmit={handleCompanySubmit}>
            <Stack spacing={1}>
              <TextField
                label="Identificación fiscal (RFC/NIT/RUT)"
                name="tax_id"
                value={companyData.tax_id}
                onChange={e => setCompanyData({ ...companyData, tax_id: e.target.value })}
                fullWidth
                margin="dense"
                required
                size="small"
                sx={{
                  background: "#f1f5f9",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e7ef" },
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                    input: { color: "#334155" },
                  },
                  label: { color: "#64748b" },
                }}
              />
              <TextField
                label="Nombre de la empresa"
                name="company"
                value={companyData.company}
                onChange={e => setCompanyData({ ...companyData, company: e.target.value })}
                fullWidth
                margin="dense"
                required
                size="small"
                sx={{
                  background: "#f1f5f9",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e7ef" },
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                    input: { color: "#334155" },
                  },
                  label: { color: "#64748b" },
                }}
              />
              <TextField
                label="Dirección"
                name="address"
                value={companyData.address}
                onChange={e => setCompanyData({ ...companyData, address: e.target.value })}
                fullWidth
                margin="dense"
                required
                size="small"
                sx={{
                  background: "#f1f5f9",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e7ef" },
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                    input: { color: "#334155" },
                  },
                  label: { color: "#64748b" },
                }}
              />
              <TextField
                select
                label=""
                name="country"
                value={companyData.country}
                onChange={e => {
                  setCompanyData({ ...companyData, country: e.target.value, city: "" });
                }}
                fullWidth
                margin="dense"
                required
                SelectProps={{ native: true }}
                size="small"
                sx={{
                  background: "#f1f5f9",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e7ef" },
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                    input: { color: "#334155" },
                  },
                  label: { color: "#64748b" },
                }}
              >
                <option value="">Selecciona un país</option>
                {countryOptions.map(c => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </TextField>
              <TextField
                select
                label=""
                name="city"
                value={companyData.city}
                onChange={e => setCompanyData({ ...companyData, city: e.target.value })}
                fullWidth
                margin="dense"
                required
                SelectProps={{ native: true }}
                disabled={!companyData.country}
                size="small"
                sx={{
                  background: "#f1f5f9",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e7ef" },
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                    input: { color: "#334155" },
                  },
                  label: { color: "#64748b" },
                }}
              >
                <option value="">Selecciona una ciudad</option>
                {(citiesByCountry[selectedCountryCode] || []).map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </TextField>
            </Stack>
            {error && (
              <Typography color="error" sx={{ mb: 1, fontSize: 13 }}>
                {error}
              </Typography>
            )}
            {success && (
              <Typography color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                {success}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="small"
              sx={{
                mt: 2,
                background: "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(90deg, #0ea5e9 0%, #6366f1 100%)",
                },
              }}
            >
              Registrar y continuar
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              fullWidth
              size="small"
              sx={{
                mt: 1,
                mb: 1,
                borderColor: "#e0e7ef",
                color: "#64748b",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#6366f1",
                  color: "#6366f1",
                  background: "#f1f5f9",
                },
              }}
              onClick={() => setShowCompanyForm(false)}
            >
              Cancelar
            </Button>
          </form>
        </Paper>
      )}
    </Box>
  );
}